import React, { useState, useEffect } from 'react';
import { Phone, Tag, Volume2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface AICallStepConfigProps {
  config: any;
  onChange: (config: any) => void;
}

interface AICallTemplate {
  id: string;
  name: string;
  script: string;
  voice_id?: string;
}

const MERGE_TAGS = [
  { tag: '{{first_name}}', label: 'First Name' },
  { tag: '{{last_name}}', label: 'Last Name' },
  { tag: '{{company_name}}', label: 'Company Name' },
  { tag: '{{phone}}', label: 'Phone' },
  { tag: '{{service_type}}', label: 'Service Type' },
  { tag: '{{agreement_status}}', label: 'Agreement Status' },
];

const VOICE_OPTIONS = [
  { id: 'alloy', name: 'Alloy', gender: 'Neutral', description: 'Clear and professional' },
  { id: 'echo', name: 'Echo', gender: 'Male', description: 'Warm and friendly' },
  { id: 'fable', name: 'Fable', gender: 'Male', description: 'Authoritative' },
  { id: 'onyx', name: 'Onyx', gender: 'Male', description: 'Deep and confident' },
  { id: 'nova', name: 'Nova', gender: 'Female', description: 'Energetic and clear' },
  { id: 'shimmer', name: 'Shimmer', gender: 'Female', description: 'Smooth and pleasant' },
];

const CALL_OBJECTIVES = [
  'Appointment Reminder',
  'Follow-up Call',
  'Payment Reminder',
  'Customer Survey',
  'Service Renewal',
  'Emergency Alert',
  'General Information',
];

export const AICallStepConfig: React.FC<AICallStepConfigProps> = ({ config, onChange }) => {
  const [templates, setTemplates] = useState<AICallTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState<AICallTemplate | null>(null);

  useEffect(() => {
    loadTemplates();
  }, []);

  useEffect(() => {
    if (config.template_id && templates.length > 0) {
      const template = templates.find(t => t.id === config.template_id);
      setSelectedTemplate(template || null);
    }
  }, [config.template_id, templates]);

  const loadTemplates = async () => {
    try {
      const { data, error } = await supabase
        .from('marketing_ai_call_templates')
        .select('*')
        .order('name');

      if (error) throw error;
      setTemplates(data || []);
    } catch (error) {
      console.error('Error loading templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateChange = (templateId: string) => {
    const template = templates.find(t => t.id === templateId);
    onChange({
      ...config,
      template_id: templateId,
      script: template?.script || config.script || '',
      voice_id: template?.voice_id || config.voice_id || 'alloy',
    });
  };

  const insertMergeTag = (tag: string) => {
    const currentValue = config.script || '';
    onChange({
      ...config,
      script: currentValue + tag,
    });
  };

  return (
    <div className="space-y-6">
      {/* Template Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          AI Call Template *
        </label>
        {loading ? (
          <div className="text-sm text-gray-500">Loading templates...</div>
        ) : (
          <select
            value={config.template_id || ''}
            onChange={(e) => handleTemplateChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">Select a template...</option>
            {templates.map(template => (
              <option key={template.id} value={template.id}>
                {template.name}
              </option>
            ))}
          </select>
        )}
        {templates.length === 0 && !loading && (
          <p className="mt-2 text-sm text-amber-600">
            No AI call templates found. Create templates in the Templates page first.
          </p>
        )}
      </div>

      {/* Call Objective */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Call Objective *
        </label>
        <select
          value={config.objective || ''}
          onChange={(e) => onChange({ ...config, objective: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="">Select objective...</option>
          {CALL_OBJECTIVES.map(objective => (
            <option key={objective} value={objective}>
              {objective}
            </option>
          ))}
        </select>
      </div>

      {/* Call Script */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-gray-700">
            Call Script *
          </label>
          <div className="flex gap-1">
            {MERGE_TAGS.slice(0, 3).map(({ tag, label }) => (
              <button
                key={tag}
                type="button"
                onClick={() => insertMergeTag(tag)}
                className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                title={`Insert ${label}`}
              >
                <Tag className="w-3 h-3" />
              </button>
            ))}
          </div>
        </div>
        <textarea
          value={config.script || ''}
          onChange={(e) => onChange({ ...config, script: e.target.value })}
          placeholder="Enter the AI call script..."
          rows={8}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none font-mono text-sm"
        />
        <p className="mt-1 text-xs text-gray-500">
          Write a natural conversation script. The AI will adapt the flow based on responses.
        </p>
      </div>

      {/* Voice Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Voice *
        </label>
        <div className="grid grid-cols-1 gap-2">
          {VOICE_OPTIONS.map(voice => (
            <label
              key={voice.id}
              className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                config.voice_id === voice.id
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <input
                type="radio"
                name="voice"
                value={voice.id}
                checked={config.voice_id === voice.id}
                onChange={(e) => onChange({ ...config, voice_id: e.target.value })}
                className="text-blue-600 focus:ring-blue-500"
              />
              <div className="ml-3 flex-1">
                <div className="flex items-center gap-2">
                  <Volume2 className="w-4 h-4 text-gray-400" />
                  <span className="font-medium text-sm">{voice.name}</span>
                  <span className="text-xs text-gray-500">({voice.gender})</span>
                </div>
                <p className="text-xs text-gray-600 mt-0.5">{voice.description}</p>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Call Settings */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Max Call Duration (minutes)
          </label>
          <input
            type="number"
            value={config.max_duration || 5}
            onChange={(e) => onChange({ ...config, max_duration: parseInt(e.target.value) })}
            min="1"
            max="15"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Max Retry Attempts
          </label>
          <input
            type="number"
            value={config.max_retries || 2}
            onChange={(e) => onChange({ ...config, max_retries: parseInt(e.target.value) })}
            min="0"
            max="5"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Call Behavior */}
      <div className="space-y-3">
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={config.leave_voicemail || true}
            onChange={(e) => onChange({ ...config, leave_voicemail: e.target.checked })}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="ml-2 text-sm text-gray-700">Leave voicemail if no answer</span>
        </label>
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={config.detect_answering_machine || true}
            onChange={(e) => onChange({ ...config, detect_answering_machine: e.target.checked })}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="ml-2 text-sm text-gray-700">Detect answering machines</span>
        </label>
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={config.record_call || false}
            onChange={(e) => onChange({ ...config, record_call: e.target.checked })}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="ml-2 text-sm text-gray-700">Record calls for quality assurance</span>
        </label>
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={config.transcribe_call || false}
            onChange={(e) => onChange({ ...config, transcribe_call: e.target.checked })}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="ml-2 text-sm text-gray-700">Generate call transcripts</span>
        </label>
      </div>

      {/* Voicemail Script */}
      {config.leave_voicemail && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Voicemail Script
          </label>
          <textarea
            value={config.voicemail_script || ''}
            onChange={(e) => onChange({ ...config, voicemail_script: e.target.value })}
            placeholder="Script to leave if call goes to voicemail..."
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
          />
          <p className="mt-1 text-xs text-gray-500">
            Keep voicemail messages brief (30-45 seconds maximum)
          </p>
        </div>
      )}

      {/* Call Timing */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Earliest Call Time
          </label>
          <input
            type="time"
            value={config.earliest_call_time || '09:00'}
            onChange={(e) => onChange({ ...config, earliest_call_time: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Latest Call Time
          </label>
          <input
            type="time"
            value={config.latest_call_time || '18:00'}
            onChange={(e) => onChange({ ...config, latest_call_time: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Available Merge Tags */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="text-sm font-medium text-gray-700 mb-2">Available Merge Tags</h4>
        <div className="grid grid-cols-2 gap-2">
          {MERGE_TAGS.map(({ tag, label }) => (
            <div key={tag} className="text-xs">
              <code className="bg-white px-2 py-1 rounded border border-gray-200">
                {tag}
              </code>
              <span className="ml-2 text-gray-600">{label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Compliance Notice */}
      <div className="bg-blue-50 rounded-lg p-4">
        <h4 className="text-sm font-medium text-blue-900 mb-2">Call Compliance</h4>
        <ul className="text-xs text-blue-800 space-y-1">
          <li>• Comply with TCPA regulations for automated calls</li>
          <li>• Respect Do Not Call lists and opt-out requests</li>
          <li>• Only call between 8am-9pm in recipient's local time</li>
          <li>• Identify your company at the beginning of each call</li>
          <li>• Provide clear opt-out instructions if requested</li>
        </ul>
      </div>
    </div>
  );
};
